export const compensationEn = {
    errors: {
        notEnabled: 'Compensation claims are not set up for this organization.',
        claimNotFound: 'Claim not found.',
        profileNotFound: 'This contact has no compensation profile.',
        notContributor: 'Only contributor contacts can receive compensation.',
        notEditable: 'This claim can no longer be changed.',
        notEligible: 'No claim can be created for this contact and period.',
        cadenceLocked:
            "The period length can't be changed while submitted claims or claims with entries overlap the current period of the new length, as the same work could then be claimed twice.",
        claimExists: 'A claim for this contact and period already exists.',
        invalidClaim: 'The claim contains invalid entries.',
        accountRequired: 'Choose an account for expenses and corrections.',
        accountNotFound: 'Account {{account}} does not exist in the chart of accounts.',
        rateInUse: 'Rates assigned to contributors or used in claims cannot be removed. Deactivate them instead.',
        limitInUse:
            'Allowance limits with an individual amount for a contributor cannot be removed. Remove the amount from the contributor first.',
        invalidLimit: 'Choose an allowance limit from the compensation settings.',
        invalidIndividualLimit: 'An individual amount cannot exceed the amount of its allowance limit.',
        tooManyClaims: 'Select at most {{count}} claims at once.',
        statementsInProgress: 'The annual statements {{year}} are already being created.',
        approvalFailed: 'The claim could not be approved.',
        negativeTotal: 'Claims with a negative total cannot be approved.',
        ownClaim: 'You cannot approve your own claim. Another person allowed to approve claims has to do it.',
        missingPayee: 'The claim has no bank account for the payout.',
        revokeNotPossible:
            'The approval can no longer be revoked because the receipt was paid or transferred. Add a correction to a later claim instead.',
        costCenterRequired: 'Choose cost center 1. The chart of accounts uses regions, so every receipt needs it.',
        invalidCostCenter: 'Choose a bookable cost center of the default mandate.',
        costCenterNotBookable: "Choose a bookable cost center of one of the organization's mandates.",
        mandateRequired: 'Choose the mandate to book the claim in.',
        defaultMandateNotFound: 'Choose one of the mandates of the organization.',
        fileNotFound: 'The attached file could not be found.',
        deadlineInPast: 'The deadline cannot be in the past.',
        deadlineRequired: 'The current deadline has passed. Set a new one.',
        contactDeleted: "The contributor's contact was deleted, so the claim cannot be booked.",
        payeeNameRequired: 'Enter the account holder of the bank account.',
        payeeBicRequired: 'Enter the BIC for a bank account outside the European Economic Area.',
        returnMessageRequired: 'Tell the contributor what needs to be changed.',
        waiverDisabled: 'Donating claims is not enabled.',
        waiverCountry: 'Claims cannot be donated in the country of the organization.',
        waiverCadence:
            'Claims can only be donated with monthly or quarterly periods, so each waiver follows the work within three months.',
        waiverDonationsNotSetUp: 'Set up donations before contributors can donate their claims.',
        waiverAccountRequired: 'Choose a revenue account for donated claims.',
        waiverDonationConfirmed:
            'The approval can no longer be revoked because the donation of the waived amount is already confirmed. Add a correction to a later claim instead.',
    },
    issues: {
        invalidRate: 'Choose one of the assigned rates.',
        outsidePeriod: 'The date must lie within the claim period.',
        invalidTimeRange: 'The end time must be after the start time.',
        timeOverlap: 'Times on the same day must not overlap.',
        expensesDisabled: 'Expenses are not enabled.',
        expenseMaxAmount: 'The amount exceeds the allowed maximum.',
        expenseReceiptRequired: 'Attach a receipt.',
        payeeRequired: 'Enter the bank account for the payout.',
        waiverExceedsTotal: 'The donated amount exceeds the total of the claim.',
        oncePerEntry: 'This rate counts once per entry. Add a separate entry for each occasion.',
    },
    notifications: {
        open: 'Open claim',
        review: 'Review claim',
        periodOpenedTitle: 'Your claim for {{period}} is open',
        periodOpenedMessage: 'Record your work and expenses and submit them by {{deadline}}.',
        reminderTitle: 'Your claim for {{period}} is due',
        reminderMessage: 'Please submit it by {{deadline}}.',
        submittedTitle: 'Claim to review',
        submittedMessage: '{{name}} submitted the claim for {{period}}.',
        submittedOnBehalfByMessage: '{{submitter}} submitted the claim of {{name}} for {{period}} on their behalf.',
        returnedTitle: 'Your claim was returned',
        returnedMessage:
            'Your claim for {{period}} needs changes. Check the comments and submit it again by {{deadline}}.',
        approvedTitle: 'Your claim was approved',
        approvedMessage: 'Your claim for {{period}} over {{amount}} was approved.',
        submittedOnBehalfTitle: 'Your claim was submitted for you',
        submittedOnBehalfMessage:
            'Your organization submitted your claim for {{period}} over {{amount}} on your behalf.',
        revokedTitle: 'The approval of your claim was withdrawn',
        revokedMessage: 'Your organization reviews your claim for {{period}} again. Check the comments in the claim.',
        deadlineChangedTitle: 'New deadline for your claim',
        deadlineChangedMessage: 'Please submit your claim for {{period}} by {{deadline}}.',
        changedTitle: 'Your claim was changed',
        changedMessage: 'Your organization changed your claim for {{period}}, which now amounts to {{amount}}.',
        paidTitle: 'Your claim was paid out',
        paidMessage: 'Your organization paid out {{amount}} for your claim for {{period}}.',
        deletedTitle: 'Your claim was deleted',
        deletedMessage: 'Your organization deleted your claim for {{period}}.',
        showClaims: 'Show claims',
        showDocuments: 'Show documents',
        statementTitle: 'Your annual statement {{year}} is available',
        statementMessage: 'Your organization shared the summary of your compensation for {{year}} in your documents.',
        statementsJobTitle: 'Creating annual statements {{year}}',
        statementsJobMessage:
            'The annual statements are being created. You will be notified when they are shared with the contributors.',
        statementsCreatedTitle: 'Annual statements {{year}} created',
        statementsCreatedMessage_one: '{{count}} contributor received an annual statement in their documents.',
        statementsCreatedMessage_other: '{{count}} contributors received an annual statement in their documents.',
        statementsCreatedProvisionalMessage_one:
            '{{count}} contributor received a provisional annual statement, as claims for the year can still be submitted.',
        statementsCreatedProvisionalMessage_other:
            '{{count}} contributors received a provisional annual statement, as claims for the year can still be submitted.',
        statementsFailedTitle: 'Annual statements {{year}} could not be created',
        statementsFailedMessage: 'Some contributors may not have received their statement yet. Create them again.',
        documentsEmptyTitle: 'No ZIP file of the claims {{year}}',
        documentsEmptyMessage: 'There are no approved claims with documents in {{year}} anymore.',
        documentsUnreadableMessage: 'None of these claim documents could be read: {{numbers}}',
        documentsFailedTitle: 'ZIP file of the claims {{year}} could not be created',
        documentsFailedMessage: 'The file could not be finished. Start the download again.',
        deadlineTitle: 'Submission deadline for {{period}} ends today',
        deadlineMessage: 'Not submitted yet: {{notSubmitted}} · To review: {{toReview}}',
    },
    properties: {
        groupName: 'Claim',
        contactName: 'Contributor',
        period: 'Period',
        status: 'Status',
        statuses: {
            draft: 'Draft',
            submitted: 'To review',
            approved: 'Approved',
        },
        totalAmount: 'Total',
        deadlineAt: 'Deadline',
        number: 'Number',
        submittedAt: 'Submitted',
        submittedBy: 'Submitted by',
        submittedOnBehalf: 'Submitted on behalf',
        approvedAt: 'Approved',
        approvedBy: 'Approved by',
        receiptNumber: 'Receipt',
        reviewers: 'Reviewers',
    },
    export: {
        title: 'Compensation {{year}}',
        documentsFileName: 'Compensation claims {{year}}',
        documentsSkipped: 'These claim documents could not be read and are missing from the ZIP file: {{numbers}}',
        claimsFileName: 'Compensation claims',
        contributor: 'Contributor',
        birthdate: 'Date of birth',
        address: 'Address',
        socialInsuranceNumber: 'Social insurance number',
        approvedClaims: 'Approved claims',
        hours: 'Hours',
        allowance: 'Allowances',
        reimbursement: 'Reimbursements',
        payout: 'Approved amount',
        waiver: 'Donated',
        limit: 'Allowance limit',
        limitAmount: 'Amount',
        limitUsed: 'Used',
        limitExempt: 'Tax-free',
        limitTaxable: 'Taxable',
        openClaims: 'Open claims',
        claim: 'Claim',
        period: 'Period',
        statusLabel: 'Status',
        type: 'Type',
        date: 'Date',
        description: 'Description',
        details: 'Details',
        amount: 'Amount',
        types: {
            hours: 'Hours',
            flatRates: 'Flat rate',
            mileage: 'Mileage',
            expenses: 'Expense',
            corrections: 'Correction',
        },
        status: {
            draft: 'Open',
            submitted: 'To review',
            approved: 'Approved',
        },
    },
    statement: {
        documentName: 'Annual compensation statement {{year}}',
        title: 'Annual compensation statement {{year}}',
        provisional: 'Provisional',
        provisionalNotice: 'Provisional: claims for this year can still be submitted.',
        intro: 'Compensation for {{name}} in {{year}}, based on all approved claims:',
        hours: 'Hours worked',
        allowance: 'Allowances',
        reimbursement: 'Reimbursed mileage and expenses',
        payout: 'Total approved',
        waiver: 'Of which donated',
        limitUsage: '{{used}} of {{amount}}',
        limitTaxable: '{{label}}: taxable part',
        footer: 'Created on {{date}}',
    },
    document: {
        contactDocumentName: 'Claim {{number}} ({{period}})',
        fileName: 'Claim {{number}}.pdf',
        title: 'Compensation claim',
        number: 'Number',
        period: 'Period',
        contributor: 'Contributor',
        birthdate: 'Date of birth',
        address: 'Address',
        socialInsuranceNumber: 'Social insurance number',
        payee: 'Bank account',
        hours: 'Hours',
        flatRates: 'Flat rates',
        mileage: 'Mileage',
        expenses: 'Expenses',
        corrections: 'Corrections',
        date: 'Date',
        time: 'Time',
        rate: 'Rate',
        quantity: 'Quantity',
        distance: 'Distance',
        description: 'Description',
        participants: 'Participants',
        amount: 'Amount',
        total: 'Total',
        waiver: 'Of which donated',
        payout: 'Payout',
        waiverTitle: 'Waiver',
        waiverText: '{{name}} waives the payout of {{amount}} from this claim and donates it to {{organization}}.',
        limits: 'Allowance limits',
        limit: 'Allowance limit',
        limitClaim: 'This claim',
        limitExempt: 'Of which tax-free',
        limitTaxable: 'Of which taxable',
        limitYear: 'Used in {{year}}',
        limitYearValue: '{{used}} of {{amount}}',
        limitNotice:
            'As of approval, with the submitted and approved claims of the year. The annual statement covers all approved claims.',
        declaration: 'Declaration',
        submittedBy: 'Submitted electronically by {{name}} on {{date}}',
        submittedOnBehalf: 'Submitted by {{name}} on behalf of the contributor on {{date}}',
        approvedBy: 'Approved by {{name}} on {{date}}',
        receiptPosition: '{{rate}} – {{period}}',
        expensesPosition: 'Expenses – {{period}}',
        correctionsPosition: 'Corrections – {{period}}',
        receiptDescription: 'Compensation claim {{number}}',
        donationDescription: 'Waiver of claim {{number}} ({{period}})',
        receiptsNotIncluded: 'These receipts could not be added here and are stored with the claim:',
    },
}
